/**
 * Connectivity read, used to only offer "View online" for chart packs when we
 * actually have a connection. Backed by @capacitor/network on the phone app,
 * with a navigator.onLine fallback on the web.
 */
import { create } from 'zustand';
import { Network } from '@capacitor/network';

interface NetworkState {
  online: boolean;
  setOnline: (online: boolean) => void;
}

export const useNetworkStore = create<NetworkState>((set) => ({
  online: typeof navigator !== 'undefined' ? (navigator.onLine ?? true) : true,
  setOnline: (online) => set({ online }),
}));

/**
 * Start watching connectivity. Sets the current status and listens for changes.
 * Returns a cleanup that removes the listener. Safe on web (falls back to the
 * browser online/offline events).
 */
export async function initNetworkWatch(): Promise<() => void> {
  const set = useNetworkStore.getState().setOnline;
  try {
    const status = await Network.getStatus();
    set(status.connected);
    const handle = await Network.addListener('networkStatusChange', (s) => set(s.connected));
    return () => {
      void handle.remove();
    };
  } catch {
    // Web / no plugin: use the browser events.
    const on = () => set(true);
    const off = () => set(false);
    if (typeof window !== 'undefined') {
      window.addEventListener('online', on);
      window.addEventListener('offline', off);
      set(navigator?.onLine ?? true);
      return () => {
        window.removeEventListener('online', on);
        window.removeEventListener('offline', off);
      };
    }
    return () => {};
  }
}
